import { useState, useEffect } from "react"
import { MdClose } from "react-icons/md"
import { FaRegPlusSquare, FaRegCheckSquare } from "react-icons/fa"
import { CircularProgress } from "@mui/material"
import tw from "tailwind-styled-components"
import { ActionSection, ModalHeader, ScrollableContent, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles"
import axios from "axios"
import { useMutation } from "react-query"
import Config from "../../Config"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"


const settingsOptions = [
    {
        key: "MANANGE_INVENTORY",
        name: "Manage Inventory",
        description: "Allow staff to manage and update inventory records.",
    },
    {
        key: "MANANGE_TENANTS",
        name: "Manage Tenants",
        description: "Allow staff to add, manage and view tenants details.",
    },
    {
        key: "MANANGE_PROPERTY_ASSETS",
        name: 'Manage Property Assets',
        description: 'Allow staff to manage property-level assets and maintenance records.',
    },
    {
        key: "MANANGE_COMPLAINTS",
        name: "Manage Complaints",
        description: "Allow staff to manage and resolve tenant complaints.",
    },
    {
        key: "MANANGE_LEADS",
        name: "Manage Leads",
        description: "Allow staff to track and manage potential tenants leads.",
    },
    {
        key: "MANANGE_ATTENDANCES",
        name: "Manage Attendance",
        description: "Allow staff to mark attendance."
    },
    {
        key: "MANANGE_SENDNOTICES",
        name: "Manage Send Notices",
        description: "Allow staff to send important notices to tenants",
    },
    {
        key: "MANANGE_REQUESTS",
        name: "Manage Requests",
        description: "Allow staff to manage tenants move in/out requests."
    },
    {
        key: "MANANGE_COLLECTIONS",
        name: "Manage Collections",
        description: "Allow staff to manage collections."
    },
    {
        key: "MANANGE_EXPENSES",
        name: "Manage Expenses",
        description: "Allow staff to track and manage property-related expenses.",
    },

    {
        key: "MANANGE_DUES",
        name: "Track Dues",
        description: "Allow staff to track outstanding tenants dues.",
    },
    {
        key: "MANANGE_EDIT_DUES",
        name: "Edit Dues",
        description: "Allow staff to edit outstanding tenants dues.",
    },
    {
        key: "MANANGE_DELETE_DUES",
        name: "Delete Dues",
        description: "Allow staff to delete outstanding tenants dues.",
    },
    {
        key: "MANANGE_MARK_PAID",
        name: "Mark as Paid",
        description: "Allow staff to mark tenant dues as paid.",
    },
    {
        key: "MANANGE_TRANS_RESTORE",
        name: "Restore Collections",
        description: "Allow staff to restore collections."
    },
    {
        key: "VIEW_FINANCIAL_SUMMARY",
        name: 'View Financial Summary',
        description: 'Allow staff to view income, expenses and profit/loss insights.',
    },
    {
        key: "MANANGE_EXTRACHARGES",
        name: "Manage Extra Charges",
        description: "Allow staff to add and manage extra charges for tenants.",
    },
    {
        key: "MANANGE_LANDLORD",
        name: 'Manage Landlord',
        description: 'Allow staff to view and manage landlord details and payments.',
    },

    {
        key: "MANANGE_STAFF",
        name: "Manage Staff",
        description: "Allow staff to add and manage other staff members."
    },
    {
        key: "MANAGE_STAFF_PERMISSION",
        name: 'Manage Staff Permissions',
        description: 'Allow staff to assign and update access permissions for team members.',
    },
    {
        key: "MANANGE_REPORTS",
        name: "Manage Reports",
        description: "Allow staff to manage reports."
    },
    {
        key: "MANANGE_BANKS",
        name: "Manage Banks",
        description: "Allow staff to manage bank details for transactions",
    },
    {
        key: "MANAGE_OTHER_STAFF_ATTENDANCE",
        name: 'Mark Attendance for Other Staff',
        description: 'Allow staff to mark attendance on behalf of other staff members.',
    },
    {
        key: "MANAGE_FOOD",
        name: "Food Management",
        description: "Provide access to create, update, and manage food menus and meal-related information."
    },
    {
        key: "MANAGE_INVENTORY",
        name: 'Manage Food Inventory',
        description: 'Allow staff to manage food inventory, stock levels and inventory records.',
    },
    {
        key: "MANAGE_STAFF_SALARY",
        name: "Manage Staff Payables",
        description: "Allow staff to view and manage staff salary details, payments and other records."
    },
    {
        key: "MANAGE_DOCUMENTS",
        name: "Manage Document Vault",
        description: "Allow staff to upload, view, download, organize, and delete business documents and folders."
    },
    {
        key: "MANAGE_BUS_SERVICE",
        name: "Manage Bus Service",
        description: "Allow staff to scan tenant QR codes and verify whether they have opted for the bus service."
    },
    {
        key: "MANAGE_LAUNDRY",
        name: 'Manage Laundry',
        description: 'Allow staff to manage laundry services, requests and laundry records.',
    },
    {
        key: "MANAGE_VENDOR",
        name: 'Manage Vendors',
        description: 'Allow staff to manage vendors, vendor details and vendor-related records.',
    },
    {
        key: "MANAGE_HOUSEKEEPING",
        name: 'Manage Housekeeping',
        description: 'Allow staff to manage daily housekeeping tasks for rooms and common areas.',
    },
    {
        key: "MANAGE_OTHER_STAFF_HOUSEKEEPING",
        name: 'Manage Other Staff Housekeeping',
        description: 'Allow staff to record housekeeping tasks on behalf of other staff members.',
    },
]

const ALL_PERMISSIONS = settingsOptions.map(permission => permission.key);

export const rolePermissionMap = {
    [Config.STAFF_ROLES.ADMIN]: ALL_PERMISSIONS,

    [Config.STAFF_ROLES.PARTNER]: ALL_PERMISSIONS,

    [Config.STAFF_ROLES.WARDEN]: ALL_PERMISSIONS,

    [Config.STAFF_ROLES.BACK_OFFICE]: ALL_PERMISSIONS,

    [Config.STAFF_ROLES.FINANCEADMIN]: ALL_PERMISSIONS,

    // [Config.STAFF_ROLES.SALES_HEAD]: ALL_PERMISSIONS,

    [Config.STAFF_ROLES.SALES_HEAD]: [
        "MANANGE_LEADS",
        "MANANGE_TENANTS",
        "MANAGE_DOCUMENTS",
        "MANANGE_INVENTORY",
        "MANANGE_ATTENDANCES",
        "MANANGE_STAFF",
        "MANAGE_STAFF_PERMISSION",
        "MANAGE_STAFF_SALARY",
        "MANANGE_REPORTS"
    ],

    [Config.STAFF_ROLES.SALESPERSON]: [
        "MANANGE_LEADS",
        "MANANGE_TENANTS",
        "MANANGE_INVENTORY",
        "MANAGE_DOCUMENTS",
        "MANANGE_ATTENDANCES",
        "MANANGE_REPORTS"
    ],

    [Config.STAFF_ROLES.HELPDESK]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_REQUESTS",
        "MANANGE_SENDNOTICES",
        "MANANGE_TENANTS",
        "MANAGE_DOCUMENTS",
        "MANAGE_HOUSEKEEPING",
        "MANAGE_OTHER_STAFF_HOUSEKEEPING"
    ],

    [Config.STAFF_ROLES.SECURITYGUARD]: [
        "MANANGE_ATTENDANCES",
        "MANANGE_COMPLAINTS",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.BROKER]: [
        "MANANGE_LEADS",
    ],

    [Config.STAFF_ROLES.COOK]: [
        "MANAGE_FOOD",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.IT_ADMIN]: [
        "MANANGE_TENANTS",
        "MANANGE_INVENTORY",
        "MANANGE_ATTENDANCES",
        "MANANGE_COMPLAINTS",
        "MANANGE_SENDNOTICES",
        "MANANGE_STAFF",
        "MANAGE_STAFF_PERMISSION",
        "MANAGE_OTHER_STAFF_ATTENDANCE",
        "MANANGE_PROPERTY_ASSETS"
    ],

    [Config.STAFF_ROLES.BUS_INCHARGE]: [
        "MANANGE_ATTENDANCES",
        "MANANGE_COMPLAINTS",
        "MANAGE_BUS_SERVICE",
    ],

    [Config.STAFF_ROLES.MAINTENANCE_SUPERVISOR]: [
        "MANANGE_ATTENDANCES",
        "MANANGE_COMPLAINTS",
        "MANANGE_PROPERTY_ASSETS",
    ],

    [Config.STAFF_ROLES.KITCHEN_MANAGER]: [
        "MANANGE_ATTENDANCES",
        "MANAGE_FOOD",
        "MANANGE_COMPLAINTS",
        "MANANGE_SENDNOTICES",
        "MANAGE_VENDOR",
        "MANAGE_INVENTORY"
    ],

    [Config.STAFF_ROLES.FOOD_VENDOR]: [
        "MANAGE_FOOD",
    ],

    [Config.STAFF_ROLES.ELECTRICIAN]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.PLUMBER]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.CARPENTER]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.LAUNDRY]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.HOUSEKEEPING]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
        // "MANAGE_OTHER_STAFF_HOUSEKEEPING"
    ],

    [Config.STAFF_ROLES.AC_TECH]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.PURIFIER_TECH]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

    [Config.STAFF_ROLES.PAINTER]: [
        "MANANGE_COMPLAINTS",
        "MANANGE_ATTENDANCES",
        "MANAGE_HOUSEKEEPING",
    ],

};

const StaffPermissionSetting = ({
    permissionStaffLoading,
    permissionStaffMutate,
    selectedData,
    setStaffPermission,
}) => {
    const staffId = selectedData?.id
    const user = useSelector((state) => state.UserReducer.user)
    const [options, setOptions] = useState(settingsOptions.map((option) => ({ ...option, checked: false })))

    // const allowedPermissions = rolePermissionMap[selectedData?.role] || [];

    // const [options, setOptions] = useState(
    //     settingsOptions
    //         .filter(option => allowedPermissions.includes(option.key))
    //         .map(option => ({ ...option, checked: false }))
    // );

    useEffect(() => {
        const allowedPermissions = rolePermissionMap[selectedData?.role] || [];

        setOptions(
            settingsOptions
                .filter(option => allowedPermissions.includes(option.key))
                .map(option => ({ ...option, checked: false }))
        );
    }, [selectedData?.role]);


    const toggleOption = (permissionKey) => {
        setOptions((prevOptions) =>
            prevOptions.map((option) => (option.key === permissionKey ? { ...option, checked: !option.checked } : option)),
        )
    }

    const fetchPermissionsFunction = async () => {
        const response = await axios.get(`${Config.apiUrl}/client/list/staff/permission/${staffId}`, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        })
        return response.data
    }

    const handleSuccess = (res) => {
        if (res?.isSuccess) {
            const permissions = res.data;

            setOptions(prevOptions =>
                prevOptions.map(option => ({
                    ...option,
                    checked: !!(permissions & (1 << Config.PERMISSIONS[option.key])),
                }))
            );
        } else {
            toast.error("Failed to fetch permissions");
        }
    };

    const handleError = (error) => {
        toast.error("Error fetching permissions:", error)
    }

    const {
        isLoading,
        mutate: fetchPermissions,
        error,
    } = useMutation(fetchPermissionsFunction, {
        onSuccess: handleSuccess,
        onError: handleError,
    })

    useEffect(() => {
        if (staffId && user.token) {
            fetchPermissions()
        }
    }, [staffId, user.token])

    const handleSubmit = () => {
        const permissionsBinary = Array(Object.keys(Config.PERMISSIONS).length).fill("0")

        options.forEach((option) => {
            if (option.checked) {
                permissionsBinary[Config.PERMISSIONS[option.key]] = "1"
            }
        })

        permissionStaffMutate({
            staffId: staffId,
            permissions: permissionsBinary.reverse().join(""),
        })
    }

    return (
        <Container>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Staff Settings</h1>
                <button onClick={() => setStaffPermission(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            {isLoading ? (
                <div className="flex justify-center items-center my-40">
                    <CircularProgress size={40} />
                </div>
            ) : (
                <ScrollableContent className="max-h-[calc(100vh-140px)] overflow-y-auto px-6 py-2">
                    {options.map((option) => (
                        <PropertyRow key={option.key}>
                            <div>
                                <Label>{option.name}</Label>
                                <Description>{option.description}</Description>
                            </div>
                            <button onClick={() => toggleOption(option.key)}>
                                {option.checked ? (
                                    <FaRegCheckSquare size={20} className="text-primary-color font-normal text-sm" />
                                ) : (
                                    <FaRegPlusSquare size={20} className="text-gray-400 font-normal text-sm" />
                                )}
                            </button>
                        </PropertyRow>
                    ))}
                </ScrollableContent>
            )}

            <ActionSection>
                <SideCancelBtn type="button" onClick={() => setStaffPermission(false)}>
                    Cancel
                </SideCancelBtn>
                <SubmitBtn type="button" onClick={handleSubmit} disabled={permissionStaffLoading}>
                    {permissionStaffLoading ? "Processing..." : "Confirm"}
                </SubmitBtn>
            </ActionSection>
        </Container>
    )
}

const Container = tw.div`bg-white w-full h-full flex flex-col`
//const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`
const PropertyRow = tw.div`flex items-center justify-between py-3 border-b`
const Label = tw.span`text-gray-900`
const Description = tw.p`text-gray-400 text-sm`

export default StaffPermissionSetting

